import React, { useEffect, useState } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import CircularProgress from 'components/theme/CircularProgress';
import FeatherIcon from 'components/custom/FeatherIcon';
import IntlMessages from 'utils/IntlMessages';

/**
 * Componente LineGraph
 *
 * Este componente se utiliza para crear gráficos de Radar interactivos utilizando la biblioteca amCharts en una aplicación web de React.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Array} props.data - Un arreglo de objetos que contiene los datos que se mostrarán en el gráfico de líneas.
 * @param {boolean} props.loading - Un booleano que indica si los datos se están cargando.
 * @param {boolean} props.error - Un booleano que indica si se ha producido un error al cargar los datos.
 * @param {Array} props.seriesData - Un arreglo de objetos que contiene información sobre las series que se mostrarán en el gráfico.
 * @param {string} props.categoryField - El nombre del campo en los datos que se utilizará en el eje x.
 * @param {string} props.height - La altura del gráfico (por ejemplo, '300px').
 * @param {string} props.htmlId - El ID del elemento HTML en el que se renderizará el gráfico.
 * @param {boolean} props.interactive - Un booleano que indica si se habilitarán las herramientas interactivas del gráfico.
 * @param {number} props.lineWidth - El ancho de las líneas en el gráfico.
 * @param {number} props.delay - Un número que representa el retraso antes de mostrar el mensaje "No hay datos".
 * @returns {JSX.Element} - El componente LineGraph.
 */
function RadarGraph( {
  data,
  loading = false,
  error = false,
  seriesData = [],
  categoryField = 'attribute',
  height = '300px',
  htmlId = 'chartdiv',
  interactive = false,
  lineWidth = 3,
} ) {
  const [noData, setNoData] = useState( false );

  useEffect( () => {
    if ( !data || !data.length || !seriesData ) return; // No hay datos
    const chart = am4core.create( htmlId, am4charts.RadarChart );

    // Configurar eje de categoría (ángulo)
    const categoryAxis = chart.xAxes.push( new am4charts.CategoryAxis() );
    categoryAxis.dataFields.category = categoryField;

    // Configurar eje de valor (radio)
    chart.yAxes.push( new am4charts.ValueAxis() );

    // Configurar serie de radar
    seriesData.forEach( ( seriesItem ) => {
      const series = chart.series.push( new am4charts.RadarSeries() );
      series.dataFields.valueY = seriesItem.valueField;
      series.dataFields.categoryX = categoryField;
      series.name = seriesItem.name;
      series.tooltipText = '{name} - {categoryX}: {valueY.value}';
      series.strokeWidth = lineWidth;
      // Habilitar los bullets de tipo círculo
      series.bullets.push( new am4charts.CircleBullet() );
    } );

    // Habilitar el zoom y el cursor
    if ( interactive ) {
      enableTools( chart );
    }

    chart.data = data;

    return () => {
      if ( chart && chart.dispose ) chart.dispose();
    };
  }, [data, categoryField, htmlId, lineWidth, seriesData, interactive] );

  useEffect( () => {
    const thereIsntData = !!( !loading && !error && ( !data || !data.length ) );
    if ( thereIsntData === noData ) return;
    if ( !thereIsntData ) setNoData( thereIsntData );
    else {
      const timeout = setTimeout( () => {
        setNoData( thereIsntData );
      }, 2000 );
      return () => clearTimeout( timeout );
    }
  }, [data, error, loading, noData] );

  const noGraphic = loading || error || noData;

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <div
        style={{
          width: '100%',
          height: '100%',
          opacity: noGraphic ? 0 : 1,
          transition: 'opacity 1s ease-in-out',
        }}
        id={htmlId}
      />

      <div
        style={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          bottom: 0,
          left: 0,
          right: 0,
          top: 0,
          pointerEvents: noGraphic ? 'auto' : 'none',
          opacity: noGraphic ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <>
            <FeatherIcon icon="alertCircle" color="warning.main" size="50px" />
            <span className="text-warning mt-2">
              <IntlMessages id="graphError" />
            </span>
          </>
        ) : (
          <div />
        )}
        {noData && (
          <>
            <FeatherIcon icon="info" color="text.primary" size="50px" />
            <span className="text-dark mt-2">
              <IntlMessages id="graphNoData" />
            </span>
          </>
        )}
      </div>
    </div>
  );
}

const enableTools = ( chart, color = '#04A89E' ) => {
  chart.cursor = new am4charts.RadarCursor();
  chart.cursor.behavior = 'zoomX';

  // Agregar el scrollbar al gráfico de radar
  const scrollX = new am4charts.XYChartScrollbar();
  scrollX.background.fill = am4core.color( color );
  scrollX.background.fillOpacity = 0.5;
  scrollX.minHeight = 10;
  scrollX.minWidth = 10;
  chart.scrollbarX = scrollX;
  chart.legend = new am4charts.Legend();
  const exportMenu = new am4core.ExportMenu();
  exportMenu.verticalAlign = 'bottom';
  chart.exporting.menu = exportMenu;
};

export default RadarGraph;
